// =============================================================================
// ETI-115 L6 — « Envoyer au partenaire » dans la fiche prospect
// =============================================================================
// Le composant AFFICHE ce que le serveur rend et DÉCLENCHE les routes d'envoi.
// ⛔ Il ne décide ni qui est partenaire (liste rendue par le serveur : configurée des
//    deux côtés), ni qui peut envoyer (garde serveur 403) : un bouton affiché ne vaut pas
//    autorisation. Aucune liste de tenants ni nom de tenant ici (témoin dans le test).
// Prospect `transmis` : « Envoyé à … le … » + statut distant en LECTURE SEULE (le statut seul).
// Prospect envoyable + partenaire(s) proposé(s) : choix + bouton. Sinon : rien.
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import { OhSelect, OhStatusNote } from "../../ui/oh-charte";

const ROLES_ENVOI = ["admin", "manager", "super_admin"];   // miroir d'affichage ; le serveur reste juge
const ENVOYABLES = ["nouveau", "en_revue"];
const STATUT_DISTANT = {
  nouveau: "Reçu, à traiter", en_revue: "En revue", converti: "Converti",
  rejete: "Rejeté", doublon: "Doublon", transmis: "Retransmis",
};

function formatDate(d) {
  try { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; } catch { return "—"; }
}

export default function EnvoiPartenaire({ prospect, role, api, headers, onSent }) {
  const [partenaires, setPartenaires] = useState([]);
  const [choix, setChoix] = useState("");
  const [envoi, setEnvoi] = useState(null);
  const [busy, setBusy] = useState(false);
  const statut = prospect && prospect.statut;
  const autorise = ROLES_ENVOI.includes(role);
  const id = prospect && prospect.id;

  useEffect(() => {
    let vivant = true;
    (async () => {
      try {
        if (!id) return;
        if (statut === "transmis") {
          const r = await fetch(`${api}/api/metier/prospects/${id}/envoi`, { headers: headers(), credentials: "include" });
          if (vivant && r.ok) setEnvoi(await r.json());
        } else if (autorise && ENVOYABLES.includes(statut)) {
          const r = await fetch(`${api}/api/metier/prospects/envoi/partenaires`, { headers: headers(), credentials: "include" });
          if (vivant && r.ok) {
            const j = await r.json();
            const liste = Array.isArray(j.partenaires) ? j.partenaires : [];
            setPartenaires(liste);
            setChoix(liste.length === 1 ? liste[0].slug : "");
          }
        }
      } catch { /* silencieux : pas de bouton plutôt qu'un faux état */ }
    })();
    return () => { vivant = false; };
  }, [id, statut, autorise, api, headers]);

  const envoyer = useCallback(async () => {
    if (!choix || busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${api}/api/metier/prospects/${id}/envoyer`, {
        method: "POST", headers: headers(), credentials: "include",
        body: JSON.stringify({ partenaire: choix }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok) { toast.success("Prospect envoyé au partenaire"); if (onSent) onSent(j); }
      else toast.error(typeof j.detail === "string" ? j.detail : "Envoi impossible");
    } catch { toast.error("Erreur réseau"); }
    finally { setBusy(false); }
  }, [api, headers, id, choix, busy, onSent]);

  if (statut === "transmis") {
    const e = (envoi && envoi.envoi) || prospect.envoi || {};
    const vers = e.vers || prospect.transmis_vers;
    const dist = envoi && envoi.distant && envoi.distant.statut;
    return (
      <OhStatusNote variant="info" title="Envoyé à un partenaire" data-testid="envoi-partenaire-etat">
        Envoyé à {vers || "—"} le {formatDate(e.le)}.
        {dist ? ` Statut chez le partenaire : ${STATUT_DISTANT[dist] || dist}.` : ""}
      </OhStatusNote>
    );
  }
  if (!autorise || !ENVOYABLES.includes(statut) || partenaires.length === 0) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="envoi-partenaire">
      <OhSelect aria-label="Partenaire destinataire" value={choix} onChange={(e) => setChoix(e.target.value)}
        style={{ width: "auto" }} data-testid="envoi-partenaire-choix">
        <option value="">Choisir un partenaire</option>
        {partenaires.map((p) => <option key={p.slug} value={p.slug}>{p.nom || p.slug}</option>)}
      </OhSelect>
      <Button variant="outline" size="sm" disabled={!choix || busy} onClick={envoyer} data-testid="envoi-partenaire-bouton">
        <Send className="w-4 h-4 mr-1" /> Envoyer au partenaire
      </Button>
    </div>
  );
}
